import React, { useState } from 'react';
import {
  Box,
  HStack,
  SimpleGrid,
  Text,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useModelsQuery,
  useDeleteModelMutation,
  ModelListItem,
} from 'src/api/models';
import { ModelCard } from '../molecules/ModelCard';
import { ViewModelDrawer } from '../molecules/ViewModelDrawer';
import { DeleteModelDialog } from '../molecules/DeleteModelDialog';
import { CreateModelPage } from './CreateModelPage';
import { EditModelPage } from './EditModelPage';

export type ModelsView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; modelId: string };

type ModelsTabProps = {
  view: ModelsView;
  onViewChange: (view: ModelsView) => void;
};

export const ModelsTab: React.FC<ModelsTabProps> = ({ view, onViewChange }) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<ModelListItem | null>(
    null,
  );

  const { data: models = [], isLoading, isError } = useModelsQuery(customerId);
  const deleteModelMutation = useDeleteModelMutation(customerId);

  const handleShowList = () => onViewChange({ type: 'list' });
  const handleShowCreate = () => onViewChange({ type: 'create' });
  const handleShowEdit = (modelId: string) =>
    onViewChange({ type: 'edit', modelId });

  const handleQuickView = (model: ModelListItem) => {
    setSelectedModelId(model.id);
    setIsViewDrawerOpen(true);
  };

  const handleEdit = (model: ModelListItem) => handleShowEdit(model.id);

  const handleDeleteClick = (model: ModelListItem) => {
    setModelToDelete(model);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!modelToDelete) return;
    try {
      await deleteModelMutation.mutateAsync({ modelId: modelToDelete.id });
      showSuccess('Model deleted', 'The model has been removed successfully.');
      setIsDeleteDialogOpen(false);
      setModelToDelete(null);
      if (selectedModelId === modelToDelete.id) {
        setIsViewDrawerOpen(false);
        setSelectedModelId(null);
      }
    } catch {
      showError(
        'Delete failed',
        'Unable to delete the model. Please try again.',
      );
    }
  };

  const handleCloseViewDrawer = () => {
    setIsViewDrawerOpen(false);
    setSelectedModelId(null);
  };

  if (view.type === 'create') {
    return (
      <>
        <CreateModelPage onComplete={handleShowList} />
        <CustomToast
          isOpen={toastState.isOpen}
          title={toastState.title}
          description={toastState.description}
          type={toastState.type}
          duration={toastState.duration}
          onClose={hideToast}
        />
      </>
    );
  }

  if (view.type === 'edit') {
    return (
      <>
        <EditModelPage modelId={view.modelId} onComplete={handleShowList} />
        <CustomToast
          isOpen={toastState.isOpen}
          title={toastState.title}
          description={toastState.description}
          type={toastState.type}
          duration={toastState.duration}
          onClose={hideToast}
        />
      </>
    );
  }

  return (
    <>
      <Box
        bg="surface.card"
        borderRadius="16px"
        border="1px solid"
        borderColor="border.layout"
        w="100%"
        p={6}>
        <HStack justify="space-between" align="center" mb={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="lg"
            color="text.title">
            MODELS
          </Text>
          <Button
            variant="primary"
            buttonName="add-new-model"
            onClick={handleShowCreate}>
            <Icon iconName="plus" boxSize={4} mr={2} color="white" />
            Add new model (290 €/mo)
          </Button>
        </HStack>

        {isError ? (
          <Center py={16}>
            <Text color="red.500">
              We were unable to load models. Please try again later.
            </Text>
          </Center>
        ) : isLoading ? (
          <Center py={16}>
            <Spinner color="icon.green" size="lg" />
          </Center>
        ) : models.length === 0 ? (
          <Center py={16}>
            <Text color="gray.500">
              No models yet. Create one to get started.
            </Text>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            {models.map((model) => (
              <ModelCard
                key={model.id}
                model={model}
                onQuickView={handleQuickView}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>

      <ViewModelDrawer
        isOpen={isViewDrawerOpen}
        onClose={handleCloseViewDrawer}
        modelId={selectedModelId}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
      />

      <DeleteModelDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        modelName={modelToDelete?.modelName ?? ''}
        isLoading={deleteModelMutation.isLoading}
      />

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
      />
    </>
  );
};
